import React, { useEffect, useMemo, useState } from 'react';

import { HomeOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminCityRow, AdminUserDetail } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import {
  buildCatalogIdPatch,
  getCatalogIds,
  toCatalogOptions,
} from 'utils/LeadProfileCatalogHelper';

import appI18n from '../../../../config/i18n';
import {
  getCityLabelKey,
  getRegionLabelKey,
  numericToDialCode,
  parsePhoneCountryCode,
} from '../leadHelpers';
import { LeadProfileCatalogs } from '../useLeadCatalogs';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

interface Props {
  detail: AdminUserDetail;
  cities: AdminCityRow[];
  citiesLoading: boolean;
  profileCatalogs: LeadProfileCatalogs;
  onUpdated: (next: AdminUserDetail) => void;
}

const buildValues = (detail: AdminUserDetail) => {
  const phoneNumeric = parsePhoneCountryCode(detail.profile.phoneCountryCode);
  // antd-phone-input acepta string en formato E.164 ("+56999548523") y lo parsea
  // internamente. Pasar un objeto parcial deja el componente sin saber qué mostrar.
  let phone: string | undefined;
  if (detail.profile.phone && phoneNumeric) {
    phone = `+${phoneNumeric}${detail.profile.phone}`;
  }

  const catalogIds = getCatalogIds(detail);

  return {
    firstName: detail.profile.firstName,
    lastName: detail.profile.lastName,
    genderId: catalogIds.genderId ?? undefined,
    nationalityId: catalogIds.nationalityId ?? undefined,
    maritalStatusId: catalogIds.maritalStatusId ?? undefined,
    birthday: detail.profile.birthday ? dayjs(detail.profile.birthday) : null,
    regionId: detail.city?.regionId || undefined,
    cityId: detail.profile.cityId || undefined,
    address: detail.profile.address || '',
    phone,
  };
};

function BasicInfoSection({
  detail,
  cities,
  citiesLoading,
  profileCatalogs,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const countryCode = detail.user.countryCode;
  const regionLabel = t(
    `scenes.leads.components.detail_lead_modal.sections.basic_info.labels.${getRegionLabelKey(
      countryCode
    )}`
  );
  const cityLabel = t(
    `scenes.leads.components.detail_lead_modal.sections.basic_info.labels.${getCityLabelKey(
      countryCode
    )}`
  );

  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>(
    detail.city?.regionId || undefined
  );

  useEffect(() => {
    form.setFieldsValue(buildValues(detail));
    setSelectedRegionId(detail.city?.regionId || undefined);
  }, [detail, form]);

  const regions = useMemo(() => {
    const map = new Map<string, string>();
    cities.forEach((c) => {
      if (!map.has(c.regionId)) map.set(c.regionId, c.regionName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));
  }, [cities]);

  // The lead's current value is appended when the catalog no longer lists it
  // (inactive row, e.g. the migrated gender "other").
  const nationalityOptions = useMemo(
    () =>
      toCatalogOptions(
        profileCatalogs.nationalities,
        i18n.language,
        detail.nationality
      ),
    [profileCatalogs.nationalities, i18n.language, detail.nationality]
  );
  const genderOptions = useMemo(
    () =>
      toCatalogOptions(profileCatalogs.genders, i18n.language, detail.gender),
    [profileCatalogs.genders, i18n.language, detail.gender]
  );
  const maritalStatusOptions = useMemo(
    () =>
      toCatalogOptions(
        profileCatalogs.maritalStatuses,
        i18n.language,
        detail.maritalStatus
      ),
    [profileCatalogs.maritalStatuses, i18n.language, detail.maritalStatus]
  );

  const citiesForRegion = (regionId?: string) => {
    if (!regionId) return [];
    return cities
      .filter((c) => c.regionId === regionId)
      .map((c) => ({ value: c.id, label: c.cityName }));
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (saving) return;
    form.setFieldsValue(buildValues(detail));
    setSelectedRegionId(detail.city?.regionId || undefined);
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // El value del PhoneInput puede ser string E.164 (si el usuario no lo tocó
      // y conservamos el original) o un objeto { countryCode, areaCode, phoneNumber }
      // (si interactuó con el componente).
      let phoneCountryCode: string | null = null;
      let phoneNumber: string | null = null;

      if (typeof values.phone === 'string') {
        // Valor inicial sin modificar: mantenemos el original del lead.
        phoneCountryCode = detail.profile.phoneCountryCode || null;
        phoneNumber = detail.profile.phone || null;
      } else if (values.phone) {
        phoneCountryCode = values.phone.countryCode
          ? numericToDialCode(values.phone.countryCode) || null
          : null;
        phoneNumber =
          values.phone.areaCode || values.phone.phoneNumber
            ? `${values.phone.areaCode || ''}${values.phone.phoneNumber || ''}`
            : null;
      }

      const response = await LeadService.updateProfileBasic(detail.user.id, {
        firstName: values.firstName,
        lastName: values.lastName,
        // Only the catalog fields the user changed: omitted keys stay as they are.
        ...buildCatalogIdPatch(getCatalogIds(detail), {
          nationalityId: values.nationalityId,
          genderId: values.genderId,
          maritalStatusId: values.maritalStatusId,
        }),
        birthday: values.birthday
          ? dayjs(values.birthday).format('YYYY-MM-DD')
          : null,
        cityId: values.cityId || null,
        address: values.address || null,
        phone: phoneNumber,
        phoneCountryCode: phoneCountryCode,
      });

      if (response.success) {
        message.success(
          t(
            'scenes.leads.components.detail_lead_modal.sections.basic_info.messages.update_success'
          )
        );
        const refreshed = await LeadService.getById(detail.user.id);
        if (refreshed.success) {
          onUpdated(refreshed.data as AdminUserDetail);
        }
        setEditing(false);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.leads.components.detail_lead_modal.sections.basic_info.messages.update_failed'
            ),
          3.5
        );
      }
    } catch (e) {
      // validation error
    } finally {
      setSaving(false);
    }
  };

  const dateFormat = FunctionsHelper.getDateFormatByLang(i18n.language);
  const fieldsDisabled = !editing || saving;

  return (
    <Card
      className={
        editing
          ? 'lead-section-card lead-section-card--editing'
          : 'lead-section-card'
      }
      size="small"
      title={
        <Space size={8}>
          <HomeOutlined />
          <span>
            {t(
              'scenes.leads.components.detail_lead_modal.sections.basic_info.title'
            )}
          </span>
        </Space>
      }
      extra={<SectionActions editing={editing} onEdit={handleEdit} />}
    >
      <Form form={form} layout="vertical" disabled={!editing}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstName"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.firstname'
              )}
              rules={[
                {
                  required: editing,
                  message: t(
                    'scenes.leads.components.detail_lead_modal.sections.basic_info.validations.firstname_required'
                  ),
                },
              ]}
            >
              <Input
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.firstname'
                )}
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastName"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.lastname'
              )}
              rules={[
                {
                  required: editing,
                  message: t(
                    'scenes.leads.components.detail_lead_modal.sections.basic_info.validations.lastname_required'
                  ),
                },
              ]}
            >
              <Input
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.lastname'
                )}
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.email'
              )}
            >
              <Input value={detail.user.email} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="phone"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.phone'
              )}
              rules={[
                {
                  validator: (_, rawValue) => {
                    if (!rawValue) return Promise.resolve();
                    // Valor inicial llega como string E.164; lo consideramos válido
                    // hasta que el usuario lo modifique (ahí pasa a ser objeto).
                    if (typeof rawValue === 'string') return Promise.resolve();
                    const phoneValue = rawValue as any;
                    if (
                      typeof phoneValue.valid === 'function' &&
                      phoneValue.valid()
                    )
                      return Promise.resolve();
                    if (
                      typeof phoneValue.valid === 'function' &&
                      !phoneValue.valid() &&
                      phoneValue.phoneNumber === null
                    )
                      return Promise.resolve();
                    return Promise.reject(
                      new Error(
                        t(
                          'scenes.leads.components.detail_lead_modal.sections.basic_info.validations.phone_invalid'
                        )
                      )
                    );
                  },
                },
              ]}
            >
              <PhoneInput
                enableSearch
                allowClear
                disabled={fieldsDisabled}
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.phone'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="genderId"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.gender'
              )}
            >
              <Select
                allowClear
                loading={profileCatalogs.gendersLoading}
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.gender'
                )}
                disabled={fieldsDisabled || profileCatalogs.gendersLoading}
                options={genderOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="birthday"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.birthday'
              )}
            >
              <DatePicker
                style={{ width: '100%' }}
                format={dateFormat}
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.birthday'
                )}
                disabled={fieldsDisabled}
                disabledDate={(d) => d && d.isAfter(dayjs())}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="nationalityId"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.nationality'
              )}
            >
              <Select
                showSearch
                allowClear
                loading={profileCatalogs.nationalitiesLoading}
                disabled={
                  fieldsDisabled || profileCatalogs.nationalitiesLoading
                }
                options={nationalityOptions}
                optionFilterProp="label"
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.nationality'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="maritalStatusId"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.marital_status'
              )}
            >
              <Select
                allowClear
                loading={profileCatalogs.maritalStatusesLoading}
                disabled={
                  fieldsDisabled || profileCatalogs.maritalStatusesLoading
                }
                options={maritalStatusOptions}
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.marital_status'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="regionId" label={regionLabel}>
              <Select
                showSearch
                allowClear
                loading={citiesLoading}
                disabled={fieldsDisabled || citiesLoading}
                options={regions}
                optionFilterProp="label"
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.region'
                )}
                onChange={(val) => {
                  setSelectedRegionId(val);
                  form.setFieldValue('cityId', undefined);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="cityId"
              label={cityLabel}
              dependencies={['regionId']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const region = getFieldValue('regionId');
                    if (region && !value) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.leads.components.detail_lead_modal.sections.basic_info.validations.city_required_when_region'
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select
                showSearch
                allowClear
                loading={citiesLoading}
                disabled={fieldsDisabled || citiesLoading || !selectedRegionId}
                options={citiesForRegion(selectedRegionId)}
                optionFilterProp="label"
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.city'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="address"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.basic_info.labels.address'
              )}
            >
              <Input
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.basic_info.placeholders.address'
                )}
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
        </Row>

        {editing && (
          <SectionEditFooter
            saving={saving}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}
      </Form>
    </Card>
  );
}

export default BasicInfoSection;
